import { periodContaining, demandIntervalContaining } from './periods';
import { coverage, withLineage } from './lineage';
import { round, calculateDemand } from './energyMath';

const empty = () => ({ positive: 0, negative: 0, covered: 0, segments: 0 });
function piece(a, b, start, end) {
  const from = Math.max(a.at, start); const to = Math.min(b.at, end);
  if (to <= from || b.at - a.at > 15000 || b.at <= a.at) return empty();
  const left = a.kw + (b.kw - a.kw) * (from - a.at) / (b.at - a.at);
  const right = a.kw + (b.kw - a.kw) * (to - a.at) / (b.at - a.at);
  const area = (x, y, duration) => (x + y) / 2 * duration / 3600000;
  let positive = 0; let negative = 0;
  if (left * right < 0) {
    const zero = from + (to - from) * Math.abs(left) / (Math.abs(left) + Math.abs(right));
    const first = area(left, 0, zero - from); const last = area(0, right, to - zero);
    positive = Math.max(first, 0) + Math.max(last, 0); negative = Math.max(-first, 0) + Math.max(-last, 0);
  } else { const value = area(left, right, to - from); positive = Math.max(value, 0); negative = Math.max(-value, 0); }
  return { positive, negative, covered: (to - from) / 1000, segments: 1, firstEnergyMessageId: a.identity, lastEnergyMessageId: b.identity,
    firstSampleTime: a.at, lastSampleTime: b.at, lastObservedAt: b.observedAt };
}
const add = (a, b) => ({ positive: a.positive + b.positive, negative: a.negative + b.negative, covered: a.covered + b.covered, segments: a.segments + b.segments, firstEnergyMessageId: a.firstEnergyMessageId ?? b.firstEnergyMessageId, lastEnergyMessageId: b.lastEnergyMessageId ?? a.lastEnergyMessageId,
  firstSampleTime: a.firstSampleTime ?? b.firstSampleTime, lastSampleTime: b.lastSampleTime ?? a.lastSampleTime, lastObservedAt: b.lastObservedAt ?? a.lastObservedAt });

// Streaming raw acquisition observations are reduced in the worker, not retained
// as millions of telemetry documents. No source point is interpolated over 15s.
export class PowerHistoryStore {
  constructor(windows, configuration = {}) {
    configuration ||= {};
    this.windows = windows; this.configuration = configuration; this.cutoff = Date.parse(windows.cutoff);
    this.previous = new Map(); this.pending = []; this.totals = new Map(); this.hours = new Map(); this.chart = new Map();
    this.hourBoundary = new Map(); this.pointCount = 0; this.maxPointAt = null;
    this.storage = new Map(); this.stats = new Map(); this.extras = new Map();
    this.demand = new Map(); this.demandBoundary = new Map(); this.recent = new Map();
    this.streaming = true;
    this.openDemand = demandIntervalContaining(windows.cutoff, windows.timezone, configuration.demandIntervalMinutes || 15);
    const ranges = [windows.selected, windows.today, windows.billing, windows.lease];
    const firstHour = periodContaining(windows.selected.start, 'hour', windows.timezone);
    const lastHour = periodContaining(windows.selected.end, 'hour', windows.timezone);
    if (windows.selected.start !== firstHour.periodStart) ranges.push({ start: windows.selected.start, end: firstHour.periodEnd });
    if (windows.selected.end !== windows.cutoff && windows.selected.end !== lastHour.periodStart) ranges.push({ start: lastHour.periodStart, end: windows.selected.end });
    this.ranges = [...new Map(ranges.map(range => {
      const target = { start: Date.parse(range.start), end: range.end === windows.cutoff ? Infinity : Date.parse(range.end) };
      return [`${target.start}:${target.end}`, target];
    })).values()];
  }

  fork() {
    const clone = Object.create(PowerHistoryStore.prototype);
    Object.assign(clone, this, { previous: new Map(this.previous), pending: this.pending.map(item => ({ ...item })),
      storage: new Map(this.storage), stats: new Map(this.stats), extras: new Map(this.extras), totals: new Map(this.totals), hours: new Map(this.hours), chart: new Map(this.chart), hourBoundary: new Map(this.hourBoundary),
      demand: new Map(this.demand), demandBoundary: new Map(this.demandBoundary), recent: new Map(this.recent) });
    return clone;
  }

  compatible(windows, configuration) {
    return JSON.stringify(configuration) === JSON.stringify(this.configuration)
      && windows.timezone === this.windows.timezone && windows.selected.bucketSeconds === this.windows.selected.bucketSeconds
      && ['selected', 'today', 'billing', 'lease'].every(key => windows[key].start === this.windows[key].start)
      && (this.windows.selected.end === this.windows.cutoff ? windows.selected.end === windows.cutoff : windows.selected.end === this.windows.selected.end);
  }

  add(row) {
    if (['requestedKw', 'constrainedKw', 'pcsKw'].some(key => typeof row[key] === 'number') && row.sampleTime != null && (row.dataStatus == null || row.dataStatus === 'available')) this.chartExtras(row);
    if (row.sampleTime == null || row.rawValue == null || (row.dataStatus != null && row.dataStatus !== 'available')) return;
    const at = Date.parse(row.sampleTime);
    if (!Number.isFinite(at) || typeof row.rawValue !== 'number' || !Number.isFinite(row.rawValue)) return;
    const point = { at, kw: row.rawValue / (row.unit === 'W' ? 1000 : 1), socPct: row.socPct,
      identity: row.energyMessageId, observedAt: row.observedAt, deviceId: row.deviceId, messageType: row.messageType, storedEnergyKwh: row.storedEnergyKwh,
      physicalId: row.equipmentId && row.counterEquipmentId ? `${row.equipmentId}:${row.counterEquipmentId}` : null };
    const prior = this.previous.get(row.deviceId);
    if (prior && at < prior.at) throw new Error('REBOOTSTRAP_REQUIRED');
    if (prior && at === prior.at) {
      if (point.kw !== prior.kw) throw new Error('REBOOTSTRAP_REQUIRED');
      return;
    }
    this.maxPointAt = Math.max(this.maxPointAt ?? -Infinity, at);
    this.pointCount += 1;
    if (this.hours.size > 600000 || this.chart.size + this.extras.size > 150000) throw new Error('This period exceeds the browser calculation budget. Select a shorter period.');
    if (prior) {
      const segment = { a: prior, b: point, through: prior.at };
      this.consume(segment, this.cutoff);
      if (segment.through < point.at) this.pending.push(segment);
    }
    this.previous.set(row.deviceId, point);
    if (this.streaming && at >= Date.parse(this.openDemand.periodStart) - 15000) this.recent.set(row.deviceId, [...(this.recent.get(row.deviceId) || []), point]);
    if (at <= this.cutoff) { this.updateStats(point, prior); this.chartPoint(point, prior); } else this.pending.push({ point, prior });
    if (row.messageType === 'load.telemetry' && typeof row.unservedKw === 'number') {
      this.add({ ...row, deviceId: `${row.deviceId}:unserved`, rawValue: row.unservedKw, unservedKw: null, messageType: 'unserved' });
    }
  }

  consume(segment, cutoff) {
    const to = Math.min(segment.b.at, cutoff); const from = segment.through;
    if (to <= from) return;
    if (segment.b.at - segment.a.at > 15000) { segment.through = to; return; }
    const id = segment.b.deviceId;
    for (const range of this.ranges) {
      const key = `${id}:${range.start}:${range.end}`;
      this.totals.set(key, add(this.totals.get(key) || empty(), piece(segment.a, segment.b, Math.max(from, range.start), Math.min(to, range.end))));
    }
    let cursor = from;
    while (cursor < to) {
      let hour = this.hourBoundary.get(id);
      if (!hour || cursor < hour.start || cursor >= hour.end) {
        const period = periodContaining(new Date(cursor).toISOString(), 'hour', this.windows.timezone);
        hour = { start: Date.parse(period.periodStart), end: Date.parse(period.periodEnd) }; this.hourBoundary.set(id, hour);
      }
      const end = Math.min(hour.end, to);
      const key = `${id}:${hour.start}`;
      this.hours.set(key, { start: hour.start, end: hour.end, deviceId: id,
        ...add(this.hours.get(key) || empty(), piece(segment.a, segment.b, cursor, end)) });
      cursor = end;
    }
    if (['load.telemetry', 'pcc.telemetry'].includes(segment.b.messageType)) {
      let cursor = from;
      while (cursor < to) {
        let interval = this.demandBoundary.get(id);
        if (!interval || cursor < interval.start || cursor >= interval.end) {
          const bounds = demandIntervalContaining(new Date(cursor).toISOString(), this.windows.timezone, this.configuration.demandIntervalMinutes || 15);
          interval = { start: Date.parse(bounds.periodStart), end: Date.parse(bounds.periodEnd) }; this.demandBoundary.set(id, interval);
        }
        const end = Math.min(interval.end, to); const key = `${id}:${interval.start}`;
        this.demand.set(key, { start: interval.start, end: interval.end, deviceId: id,
          ...add(this.demand.get(key) || empty(), piece(segment.a, segment.b, cursor, end)) });
        cursor = end;
      }
    }
    segment.through = to;
  }

  setCutoff(value) {
    const cutoff = Date.parse(value);
    if (cutoff < this.cutoff) throw new Error('REBOOTSTRAP_REQUIRED');
    this.cutoff = cutoff;
    const pending = [];
    for (const item of this.pending) {
      if (item.extra) { if (Date.parse(item.extra.sampleTime) <= cutoff) this.chartExtras(item.extra); else pending.push(item); }
      else if (item.point) { if (item.point.at <= cutoff) { this.updateStats(item.point, item.prior); this.chartPoint(item.point, item.prior); } else pending.push(item); }
      else { this.consume(item, cutoff); if (item.through < item.b.at) pending.push(item); }
    }
    this.pending = pending;
  }

  rememberStorage(point) {
    if (point.messageType !== 'bess.telemetry' || point.at < Date.parse(this.windows.selected.start)
      || (point.at > Date.parse(this.windows.selected.end) && this.windows.selected.end !== this.windows.cutoff)) return;
    const old = this.storage.get(point.deviceId); const ids = [...new Set([...(old?.physicalIds || []), point.physicalId])];
    this.storage.set(point.deviceId, { start: old?.start || (point.at === Date.parse(this.windows.selected.start) ? point : null), end: point, physicalIds: ids });
  }

  storageEndpoints(deviceId, start, end) {
    const stored = this.storage.get(deviceId);
    if (!stored) return null;
    return { start: stored.start?.at === Date.parse(start) ? stored.start : null,
      end: stored.end?.at === Date.parse(end) ? stored.end : null,
      physicalUnitUnchanged: stored.physicalIds.length === 1 && stored.physicalIds[0] != null };
  }

  updateStats(point, prior) {
    this.rememberStorage(point);
    const kinds = ['demand_interval', 'hour', 'day', 'billing_period'];
    for (const kind of kinds) {
      const key = `${point.deviceId}:${kind}`; const old = this.stats.get(key);
      let bounds = old;
      if (!bounds || point.at < bounds.start || point.at >= bounds.end) {
        const period = kind === 'demand_interval' ? demandIntervalContaining(new Date(point.at).toISOString(), this.windows.timezone, this.configuration.demandIntervalMinutes || 15)
          : periodContaining(new Date(point.at).toISOString(), kind, this.windows.timezone, { billingStartDay: this.configuration.billingStartDay || this.configuration.billingPeriodStartDay });
        bounds = { start: Date.parse(period.periodStart), end: Date.parse(period.periodEnd) };
      }
      const same = old?.start === bounds.start; const count = (same ? old.sampleCount : 0) + 1;
      const integrated = prior ? piece(prior, point, bounds.start, bounds.end) : empty();
      const total = add(same ? old : empty(), integrated);
      this.stats.set(key, { ...bounds, ...total, deviceId: point.deviceId, messageType: point.messageType, kind, sampleCount: count,
        runningMeanKw: same ? old.runningMeanKw + (point.kw - old.runningMeanKw) / count : point.kw,
        minKw: Math.min(same ? old.minKw : Infinity, point.kw), maxKw: Math.max(same ? old.maxKw : -Infinity, point.kw),
        lastSampleTime: new Date(point.at).toISOString(), lastPowerKw: point.kw, lastIdentity: point.identity });
    }
  }

  analytics(context = {}) {
    const result = {};
    for (const stats of this.stats.values()) {
      const end = Math.min(stats.end, this.cutoff); if (end < stats.start) continue;
      const startTime = new Date(stats.start).toISOString();

      const role = { 'site.telemetry': 'site', 'canopy.telemetry': 'canopy', 'bess.telemetry': 'bess', 'load.telemetry': 'load', 'pcc.telemetry': 'pcc' }[stats.messageType] || stats.messageType;
      result[`${role}:${stats.deviceId}:${stats.kind}`] = { ...stats, windowStart: startTime, windowEnd: new Date(stats.end).toISOString(),
        validSegmentSeconds: stats.covered, positiveKwhSum: stats.positive, negativeKwhSum: stats.negative,
        sourceWatermark: { lastEnergyMessageId: stats.lastIdentity, lastSampleTime: stats.lastSampleTime }, watermarkCommitSeq: context.confirmedCommitSeq };
    }
    return result;
  }

  chartExtras(row) {
    const at = Date.parse(row.sampleTime); const width = this.windows.selected.bucketSeconds * 1000;
    if (!Number.isFinite(at)) return;
    this.maxPointAt = Math.max(this.maxPointAt ?? -Infinity, at);
    if (at < Date.parse(this.windows.selected.start) || (at > Date.parse(this.windows.selected.end) && this.windows.selected.end !== this.windows.cutoff)) return;
    if (at > this.cutoff) { this.pending.push({ extra: row }); return; }
    if (row.messageType === 'bess.telemetry') {
      const selector = this.configuration.authoritativeSources?.battery;
      const ids = typeof selector === 'string' ? [selector] : selector?.deviceIds || [selector?.deviceId];
      if (ids.length !== 1 || ids[0] !== row.deviceId) return;
    }
    for (const field of ['requestedKw', 'constrainedKw', 'pcsKw']) {
      if (typeof row[field] !== 'number' || !Number.isFinite(row[field])) continue;
      const bucket = Math.floor(at / width) * width; const key = `${field}:${row.deviceId}:${bucket}`;
      const old = this.extras.get(key);
      this.extras.set(key, { at: bucket, field, deviceId: row.deviceId, count: (old?.count || 0) + 1, total: (old?.total || 0) + row[field] });
    }
  }

  chartPoint(point, prior) {
    const allowed = ['generation', 'battery', 'servedLoad', 'pcc'].flatMap(role => {
      const selector = this.configuration.authoritativeSources?.[role];
      return typeof selector === 'string' ? [selector] : selector?.deviceIds || (selector?.deviceId ? [selector.deviceId] : []);
    });
    if (!allowed.includes(point.deviceId)) return;
    const width = this.windows.selected.bucketSeconds * 1000;
    const bucket = Math.floor(point.at / width) * width;
    if (point.at < Date.parse(this.windows.selected.start) || (point.at > Date.parse(this.windows.selected.end) && this.windows.selected.end !== this.windows.cutoff)) return;
    const key = `${point.deviceId}:${bucket}`; const old = this.chart.get(key);
    this.chart.set(key, { deviceId: point.deviceId, at: bucket, count: (old?.count || 0) + 1, total: (old?.total || 0) + point.kw,
      min: Math.min(old?.min ?? Infinity, point.kw), max: Math.max(old?.max ?? -Infinity, point.kw), socPct: point.socPct ?? old?.socPct,
      gap: Boolean(old?.gap || (prior && point.at - prior.at > 15000)) });
  }

  estimate(deviceId, start, end, direction, context) {
    const a = Date.parse(start); const b = Date.parse(end);
    const range = this.ranges.find(item => item.start === a && item.end === b) || this.ranges.find(item => item.start === a && item.end === Infinity && b === this.cutoff);
    let total = range ? this.totals.get(`${deviceId}:${a}:${range.end}`) : null;
    if (!total) {
      const demand = this.demand.get(`${deviceId}:${a}`);
      if (demand && Math.min(demand.end, this.cutoff) === b) total = demand;
    }
    if (!total) {
      total = empty();
      let cursor = a;
      while (cursor < b) {
        const bounds = periodContaining(new Date(cursor).toISOString(), 'hour', this.windows.timezone);
        const at = Date.parse(bounds.periodStart); const through = Date.parse(bounds.periodEnd);
        const hour = this.hours.get(`${deviceId}:${at}`);
        if (hour && at >= a && Math.min(through, this.cutoff) <= b) total = add(total, hour);
        cursor = through;
      }
    }
    const c = coverage(Math.max(0, (b - a) / 1000), total.covered);
    return withLineage({ value: c.coveredSeconds > 0 ? round(total[direction === 'negative' ? 'negative' : 'positive']) : null,
      unit: 'kWh', status: c.coveredSeconds === c.expectedSeconds && c.expectedSeconds > 0 ? 'complete' : c.coveredSeconds > 0 ? 'partial' : 'unavailable',
      reasonCodes: c.coveredSeconds === c.expectedSeconds ? [] : ['incompleteCoverage'], sourceKind: 'estimated', coverage: c,
      sourceWatermark: { deviceId, firstEnergyMessageId: total.firstEnergyMessageId ?? null, lastEnergyMessageId: total.lastEnergyMessageId ?? null,
        firstSampleTime: total.firstSampleTime == null ? null : new Date(total.firstSampleTime).toISOString(), lastSampleTime: total.lastSampleTime == null ? null : new Date(total.lastSampleTime).toISOString(), lastObservedAt: total.lastObservedAt ?? null },
    }, { ...context, cutoffTime: end });
  }

  series(configuration) {
    const selectors = { generation: 'solarKw', servedLoad: 'loadKw', battery: 'bessKw', pcc: 'gridKw' };
    const ids = {};
    Object.entries(selectors).forEach(([role, field]) => {
      const selector = configuration?.authoritativeSources?.[role];
      const devices = typeof selector === 'string' ? [selector] : selector?.deviceIds || (selector?.deviceId ? [selector.deviceId] : []);
      if (devices.length === 1) ids[devices[0]] = field;
    });
    const times = new Map();
    for (const bucket of this.chart.values()) {
      const field = ids[bucket.deviceId]; if (!field) continue;
      if (!times.has(bucket.at)) times.set(bucket.at, { time: new Date(bucket.at).toISOString(), solarKw: null, loadKw: null, bessKw: null, gridKw: null, socPct: null, extrema: {} });
      const target = times.get(bucket.at); target[field] = bucket.gap ? null : bucket.total / bucket.count;
      target.extrema[field] = { min: bucket.min, max: bucket.max };
      if (field === 'bessKw') target.socPct = bucket.socPct ?? null;
    }
    const extraFields = new Map();
    for (const bucket of this.extras.values()) {
      const key = `${bucket.at}:${bucket.field}`; const old = extraFields.get(key);
      extraFields.set(key, old ? { ...old, ambiguous: true } : bucket);
    }
    for (const bucket of extraFields.values()) {
      if (!times.has(bucket.at)) times.set(bucket.at, { time: new Date(bucket.at).toISOString() });
      times.get(bucket.at)[bucket.field] = bucket.ambiguous ? null : bucket.total / bucket.count;
    }
    return [...times].sort(([a], [b]) => a - b).map(([, value]) => value);
  }

  demandIntervals(deviceId, start, end, context) {
    const seconds = (this.configuration.demandIntervalMinutes || 15) * 60;
    const results = [];
    for (const interval of this.demand.values()) {
      if (interval.deviceId !== deviceId || interval.start < Date.parse(start) || interval.end > Date.parse(end)) continue;
      const evidence = coverage(seconds, interval.covered);
      const value = calculateDemand({ ...evidence, status: evidence.coveredSeconds === seconds ? 'complete' : 'partial', positiveEnergyKwh: interval.positive }, seconds);
      results.push(withLineage({ ...value, sourceKind: 'estimated', method: 'sampledPowerEstimate', coverage: evidence,
        cutoffTime: new Date(interval.end).toISOString(), sourceWatermark: { deviceId, firstEnergyMessageId: interval.firstEnergyMessageId, lastEnergyMessageId: interval.lastEnergyMessageId },
        intervalStart: new Date(interval.start).toISOString(), intervalEnd: new Date(interval.end).toISOString() }, context));
    }
    return results.sort((a, b) => Date.parse(a.intervalStart) - Date.parse(b.intervalStart));
  }
}
